import {api} from '@eksab/api';
import {AxiosError, AxiosResponse} from 'axios';
import {useMutation, UseMutationOptions} from '@tanstack/react-query';
import {lineupCache} from './lineupCache';
import {useLineupQuery} from './useLineupQuery';
import {State} from './usePlayersReducer';

export interface MultiEntryCompetition {
  id: number;
  name: string;
  entry_fee: number;
}

interface SubmitLineupMutationReturn {
  similar_competitions: MultiEntryCompetition[];
  success: boolean;
}

export function useSubmitLineupMutation(
  competitionId: string,
  options?: UseMutationOptions<
    SubmitLineupMutationReturn,
    AxiosError,
    {players: State['players']; substitutes: State['substitutes']; formation: State['formation']}
  >,
) {
  const lineupQuery = useLineupQuery(competitionId);
  return useMutation<
    SubmitLineupMutationReturn,
    AxiosError,
    {players: State['players']; substitutes: State['substitutes']; formation: State['formation']}
  >(
    (params) =>
      api
        .post<
          any,
          AxiosResponse<{data: SubmitLineupMutationReturn}>,
          {
            formation: Formation;
            players: {
              is_substitute: boolean;
              is_captain: 0 | 1 | undefined;
              is_vice_captain: 0 | 1 | undefined;
              id: number;
            }[];
          }
        >(`v2/competitions/${competitionId}/team-setup`, {
          formation: params.formation,
          players: [
            ...params.players[0].map((p) => ({
              id: p!.id,
              is_substitute: false,
              is_captain: p?.is_captain,
              is_vice_captain: p?.is_vice_captain,
            })),
            {
              id: params.substitutes[0]!.id,
              is_substitute: true,
              is_captain: params.substitutes[0]!.is_captain,
              is_vice_captain: params.substitutes[0]!.is_vice_captain,
            },
            ...params.players[1].map((p) => ({
              id: p!.id,
              is_substitute: false,
              is_captain: p?.is_captain,
              is_vice_captain: p?.is_vice_captain,
            })),
            {
              id: params.substitutes[1]!.id,
              is_substitute: true,
              is_captain: params.substitutes[1]?.is_captain,
              is_vice_captain: params.substitutes[1]?.is_vice_captain,
            },
            ...params.players[2].map((p) => ({
              id: p!.id,
              is_substitute: false,
              is_captain: p?.is_captain,
              is_vice_captain: p?.is_vice_captain,
            })),
            {
              id: params.substitutes[2]!.id,
              is_substitute: true,
              is_captain: params.substitutes[2]?.is_captain,
              is_vice_captain: params.substitutes[2]?.is_vice_captain,
            },
            ...params.players[3].map((p) => ({
              id: p!.id,
              is_substitute: false,
              is_captain: p?.is_captain,
              is_vice_captain: p?.is_vice_captain,
            })),
            {
              id: params.substitutes[3]!.id,
              is_substitute: true,
              is_captain: params.substitutes[3]?.is_captain,
              is_vice_captain: params.substitutes[3]?.is_vice_captain,
            },
          ],
        })
        .then((res) => res.data.data),
    {
      ...options,
      onSuccess: (...params) => {
        options?.onSuccess?.(...params);

        lineupCache.removeLineup(competitionId);
        lineupQuery.refetch();
      },
    },
  );
}
